import React, { useMemo } from 'react'
import { SUBJECT_TYPES } from '@aytm/app/insights/dashboard/lib/config'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import { Editor, SubjectView } from '@aytm/app/insights/dashboard/components/ui'

import { useValue, useRow, useDashboard } from '@aytm/app/insights/dashboard/components/hooks'

// const halfEmHeightStyle = { height: '0.5em', flex: '0 0 0.5em' }

/**
 *
 * @param {*} param0
 * @returns
 */
export default function useView({
  subjectType,
  editor: propsEditor,
  items: propsItems,
  onEditorChange,
  onImageUpload,
  onItemDelete,
  wait
}) {
  const isConcept = SUBJECT_TYPES.CONCEPT === subjectType

  // Block info
  const { blockModel } = useDashboard()

  // Component store properties

  /** Object of type { <uid>: <data>, ... } contains all subjects fetched from schema query */
  const subjects = usePool(InsightsDashboardPool, state => {
    const store = state.currentData
    return store?.subjects || null
  })

  // Block model properties

  /** Show/Hide ranks */
  const showRanks = useMemo(() => (isConcept ? blockModel?.get('showRanks') : false), [blockModel, isConcept])

  /** Show/Hide images */
  const showImages = useMemo(() => (isConcept ? blockModel?.get('showImages') : false), [isConcept, blockModel])

  /** Show/Hide chart */
  //  const showChart = useMemo(() => blockModel?.getObject('showChart') || false, [blockModel])

  /** Current split option */
  const currentSplit = useMemo(() => blockModel?.getObject('currentSplit') || null, [blockModel])
  const swap = useMemo(() => blockModel?.getObject('swap') || false, [blockModel])

  /** Set for current items */
  const items = useMemo(() => propsItems || [], [propsItems])

  /** Current editor state */
  const editor = useMemo(() => propsEditor || null, [propsEditor])

  /** getColumnData function */
  const { getColumnData } = useValue()

  /** getRow function */
  const { getRow } = useRow({ items, editor })

  return useMemo(
    () => [
      getRow(
        // 1: Produce first cell
        ({ key }) =>
          showImages ? (
            <SubjectView.Empty key={key}>
              {/* <SubjectLoader disabled={showChart} caption={showImages ? 'Updating data ...' : null} /> */}
            </SubjectView.Empty>
          ) : null,
        // 2: Produce in place subject title editors
        ({ key }) => (
          <Editor
            key={key}
            rowType={'view'}
            editor={editor}
            showImage={showImages}
            wait={wait}
            onEditorChange={onEditorChange}
            onImageUpload={onImageUpload}
          />
        ),
        // 3: Produce subject common cells
        ({ key, itemIndex, item, uid, isExtraColumn, isEditing }) => {
          const subject = subjects?.[uid]
          if (isExtraColumn) {
            return isEditing ? (
              <div key={key} />
            ) : (
              <Editor
                key={key}
                rowType={'view'}
                editor={editor}
                showImage={showImages}
                wait={wait}
                onEditorChange={onEditorChange}
                onImageUpload={onImageUpload}
              />
            )
          } else {
            return (
              <SubjectView
                key={key}
                index={itemIndex}
                item={item}
                subject={subject}
                showRank={showRanks}
                showImage={showImages}
                showImageCondensed={Boolean(currentSplit)}
                showDelete={!isEditing && !(currentSplit && swap)}
                onGetColumnData={getColumnData}
                onDelete={onItemDelete}
              />
            )
          }
        }
      )
    ],
    [
      currentSplit,
      swap,
      editor,
      getColumnData,
      getRow,
      onEditorChange,
      onImageUpload,
      onItemDelete,
      // showChart,
      showImages,
      showRanks,
      subjects,
      wait
    ]
  )
}
